import React, { useRef, useState, useEffect } from 'react';
import { auth, db, googleProvider } from "../../../firebase";
import { signInWithEmailAndPassword, signInWithPopup, onAuthStateChanged, setPersistence, browserSessionPersistence, signOut } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { Link, useNavigate, useLocation } from 'react-router-dom';

import SEO from '../../SEO';

import styleClasses from '../css/mobile/LoginSignUp.module.scss';

function Login() {
  const emailRef      = useRef();
  const passwordRef   = useRef();
  const isSubmitting  = useRef(false);
  
  const navigate      = useNavigate();
  const location      = useLocation();

  const [email, setEmail]                 = useState('');
  const [password, setPassword]           = useState('');
  const [buttonLoading, setButtonLoading] = useState(false);

  const [emailError, setEmailError]               = useState('');
  const [emailErrorText, setEmailErrorText]       = useState('');
  const [passwordError, setPasswordError]         = useState('');
  const [passwordErrorText, setPasswordErrorText] = useState('');
  const [loginError, setLoginError]               = useState('');
  const [welcome, setWelcome]                     = useState(false);

  // 음원 발매 경력: 'released' (있음) 또는 'unreleased' (없음)
  const [music, setMusic] = useState('released');
  // 팀활동 여부: 'solo' (솔로) 또는 'team' (팀)
  const [artistType, setArtistType] = useState('solo');

  // "이메일 저장" 체크박스 상태
  const [rememberEmail, setRememberEmail] = useState(false);

  // 이전 URL을 가져옵니다.
  const from = location.state?.from || '';  // `state`가 없으면 기본값 '/'로 설정

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberEmail(true);
    }
  }, []);

  // ✅ 로그인 상태 유지 체크 후 자동 리다이렉트
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // user가 존재하고 이메일 인증이 된 경우에만 리다이렉트
      if (user && user.emailVerified) {
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);
  
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.status === 'active') {
              from !== '' ? navigate(from) : navigate("/my-page");
            }
          }
        } catch (err) {
          console.error("상태 확인 오류:", err.message);
          setLoginError("로그인 상태 확인 중 오류가 발생했습니다.");
          await signOut(auth);
        }
      }
    });
    return () => unsubscribe();
  }, [navigate, from]);

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const resetError = (event) => {
    if (event.target.id === 'email') {
      setEmailError('');
      setEmailErrorText('');
    } else if (event.target.id === 'password') {
      setPasswordError('');
      setPasswordErrorText('');
    }
    setLoginError('');
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    if (!validateEmail(email)) {
      setEmailError('error');
      setEmailErrorText('올바른 이메일 주소를 입력하세요.');
      emailRef.current.focus();
      return;
    }

    if (password === '') {
      setPasswordError('error');
      setPasswordErrorText('비밀번호를 입력하세요.');
      passwordRef.current.focus();
      return;
    }

    try {
      if (isSubmitting.current) return; // 실행 중일 경우 중복 클릭 방지
      isSubmitting.current = true;
      setButtonLoading(true);

      // 인증 지속성을 session으로 설정
      await setPersistence(auth, browserSessionPersistence);

      // Firebase Auth 로그인
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // 사용자 객체를 새로 고침 (최신 인증 상태 불러오기)
      await user.reload();
      const currentUser = auth.currentUser; // 최신 사용자 정보를 가져옴

      // 이메일 인증 여부 확인: 인증되지 않았다면 로그인 차단
      if (!currentUser.emailVerified) {
        setLoginError("이메일 인증 후 로그인 가능합니다.");
        // 인증되지 않은 사용자는 반드시 로그아웃
        await signOut(auth);
        isSubmitting.current = false;
        setButtonLoading(false);
        return;
      }

      // Firestore에서 사용자 상태 확인
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        isSubmitting.current = false;
        setButtonLoading(false);

        const userData = userDoc.data();

        // 이메일 저장 체크박스 상태에 따라 로컬 스토리지 업데이트
        if (rememberEmail) {
          localStorage.setItem('rememberedEmail', email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        
        // Firestore의 status를 active로 변경
        if (userData.status === "pending") {
          await updateDoc(userDocRef, { status: "active" });
          setWelcome(true);
          return;
        } else {
          navigate("/my-page");
        }
      } else {
        isSubmitting.current = false;
        setButtonLoading(false);
        setLoginError("사용자 정보가 없습니다.");
      }
    } catch (err) {
      isSubmitting.current = false;
      setButtonLoading(false);
      setLoginError("사용자 정보가 없습니다.");
    }
  };

  // 🔹 Google 로그인 처리 함수
  const handleGoogleLogin = async () => {
    try {
      await setPersistence(auth, browserSessionPersistence);
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        navigate("/my-page");
      } else {
        navigate(`/google-consent?uid=${user.uid}&email=${user.email}&name=${user.displayName}`);
      }
    } catch (error) {
      console.error("구글 로그인 실패:", error.message);
    }
  };

  return (
    <>
      <SEO title='로그인' />
      <div className={styleClasses.loginMobile }>
        <div className={styleClasses.inner}>
          {
            welcome
            ?
            (
              <div className={ styleClasses.welcomeSection }>
                <div className={styleClasses.title}>
                  <h1>회원가입을 축하합니다.</h1>
                </div>
                <div className={styleClasses.description}>
                  {/* <img src={`${process.env.PUBLIC_URL}/assets/images/logo.webp`} alt="MusiCards" />
                  <p>나를 소개하는 가장 뮤지션다운 방법, 뮤지카드</p> */}
                  <h2 className={ styleClasses.mainTitle }>뮤지카드 생성</h2>
                  <h3 className={ styleClasses.subtitle }>간단하지만 확실한, 내 음악 활동을 보여주는 방법. <br />지금, 뮤지카드를 만들어보세요.</h3>
                </div>
                <div>
                </div>
                <div className="commonForm">
                  {/* 음원 발매 경력 라디오 버튼 */}
                  <div className="formRow fullWidth">
                    <div className="labelBox">
                      <label>음원 발매 여부 선택</label>
                    </div>
                    <div className="fieldBox checkBox">
                      <div className="eachBox">
                        <input type="radio" name="music" id="released" value="released" checked={music === 'released'} onChange={(e) => setMusic(e.target.value)} />
                        <label htmlFor='released'>
                          발매 이력 있음
                        </label>
                        <div className="commonIcon mobile toolTip">
                          <i className="questionBlack w16"></i>
                          <div className="toolTipText bottom">
                            <p>유튜브 뮤직에서 <br />검색이 가능한 경우 선택해 주세요.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="fieldBox checkBox">
                      <div className="eachBox">
                        <input type="radio" name="music" id='unreleased' value="unreleased" checked={music === 'unreleased'} onChange={(e) => setMusic(e.target.value)} />
                      <label htmlFor='unreleased'>
                        발매 이력 없음
                      </label>
                        <div className="commonIcon mobile toolTip">
                          <i className="questionBlack w16"></i>
                          <div className="toolTipText bottom">
                            <p>정식 유통은 하지 않았지만, <br />사운드 클라우드에 음원이 <br />업로드 되어 있는 경우 선택해주세요.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* 팀/솔로 라디오 버튼 */}
                  <div className="formRow fullWidth">
                    <div className="labelBox">
                      <label>활동 형태</label>
                    </div>
                    <div className="fieldBox checkBox">
                      <div className="eachBox">
                      <input type="radio" name="artistType" id='solo' value="solo" checked={artistType === 'solo'} onChange={(e) => setArtistType(e.target.value)} />
                        <label htmlFor="solo">솔로</label>
                      </div>
                    </div>
                    <div className="fieldBox checkBox">
                      <div className="eachBox">
                        <input type="radio" name="artistType" id='team' value="team" checked={artistType === 'team'} onChange={(e) => setArtistType(e.target.value)} />
                        <label htmlFor="team">팀 혹은 밴드</label>
                      </div>
                    </div>
                  </div>
                  {/* 선택한 값을 쿼리 파라미터로 전달 */}
                  <div className="commonButton fullWidth">
                    <Link to={`/my-page/musicard/create?music=${music}&type=${artistType}`} className='black'>
                      다음으로
                    </Link>
                    <Link to="/my-page" className={ styleClasses.skipButton }>Skip</Link>
                  </div>
                </div>
              </div>
            )
            :
            (
              <>
                <div className={styleClasses.title}>
                  <h1>로그인</h1>
                </div>
                <form onSubmit={handleLogin}>
                  <div className="commonForm">
                    <div className='formRow'>
                      <div className={`fieldBox borderBottom ${emailError}`}>
                        <input
                          ref={emailRef}
                          type="text"
                          id="email"
                          name="email"
                          value={email}
                          placeholder='ID (이메일)'
                          autoComplete='off'
                          onChange={(e) => { setEmail(e.target.value); resetError(e); }}
                        />
                        <div className="fieldError">
                          <p>{ emailErrorText }</p>
                        </div>
                      </div>
                    </div>
                    <div className='formRow'>
                      <div className={`fieldBox borderBottom ${passwordError}`}>
                        <input
                          ref={passwordRef}
                          type="password"
                          id="password"
                          name="password"
                          value={password}
                          placeholder='비밀번호'
                          autoComplete='off'
                          onChange={(e) => { setPassword(e.target.value); resetError(e); }}
                        />
                        <div className="fieldError">
                          <p>{ passwordErrorText }</p>
                        </div>
                      </div>
                    </div>
                    {/* 이메일 저장 체크박스 */}
                    <div className='formRow'>
                      <div className={`fieldBox checkBox ${ styleClasses.customFieldBox }`}>
                        <div className="eachBox">
                          <input
                            type="checkbox"
                            id="rememberEmail"
                            name="rememberEmail"
                            checked={rememberEmail}
                            onChange={() => setRememberEmail(prev => !prev)}
                          />
                          <label htmlFor="rememberEmail">이메일 저장</label>
                        </div>
                      </div>
                    </div>
                    { loginError && <p className={ styleClasses.loginError }>{ loginError }</p> }
                    <div className='commonButton fullWidth'>
                      <button className='black' type="submit">
                        로그인
                        <div className={`buttonLoading ${ buttonLoading && 'active' }`}>
                          <div></div>
                          <div></div>
                          <div></div>
                        </div>
                      </button>
                      <button className='black' type="button" onClick={handleGoogleLogin}>
                        <img src="/assets/icons/google-icon.png" alt="Google Icon" /> 구글 로그인
                      </button>
                      <Link to='/signup'>회원가입</Link>
                    </div>
                  </div>
                </form>
                <div className={styleClasses.customButton}>
                  <Link to='/forgot-password' className={styleClasses.link}>비밀번호 재설정</Link>
                </div>
              </>
            )
          }
        </div>
      </div>
    </>
  );
}

export default Login;